/**
 * Breeze Data Integrity Audit (admin-only) -- `/api/v1/oi-backfill/integrity-audit/*`
 * (see `app.api.v1.oi_integrity_audit`). READ-ONLY audit of stored index 1-minute / 5-minute candles and
 * 5-minute option data, plus the Multi-factor DEMA-WMA Gainer backtest data suitability. Never repairs data.
 */
import api from "@/services/api";

export type AuditUnderlying = "NIFTY" | "BANKNIFTY" | "BOTH";
export type AuditCategory = "INDEX_1M" | "INDEX_5M" | "OPTIONS_5M" | "ALL";
export type AuditRunStatus = "PENDING" | "RUNNING" | "CANCEL_REQUESTED" | "COMPLETED" | "FAILED" | "CANCELLED";
export type IntegrityStatus = "PASS" | "WARNING" | "FAIL" | "NO_DATA" | "NOT_EVALUATED" | "INFO";
export const ACTIVE_AUDIT_STATUSES: AuditRunStatus[] = ["PENDING", "RUNNING", "CANCEL_REQUESTED"];

export const CATEGORY_LABEL: Record<AuditCategory, string> = {
  INDEX_1M: "Index 1-minute candles",
  INDEX_5M: "Index 5-minute candles",
  OPTIONS_5M: "Options 5-minute candles",
  ALL: "All datasets",
};
export const DATASET_LABEL: Record<string, string> = {
  INDEX_1M: "Index 1-minute",
  INDEX_5M: "Index 5-minute",
  OPTIONS_5M: "Options 5-minute",
  CROSS_1M_5M: "1-min vs 5-min cross-check",
  DEMA_WMA: "DEMA-WMA backtest suitability",
};

export interface AuditMetrics {
  expected_candles: number | null;
  actual_candles: number | null;
  missing_candles: number | null;
  missing_pct: number | null;
  duplicate_records: number;
  invalid_ohlc: number;
  timestamp_errors: number;
  cross_mismatches: number | null;
  /** HISTORICAL candles of the backtest's contracts, excluding slots in which the contract did not trade */
  options_coverage_pct: number | null;
  options_historical_all_slots_pct?: number | null;
  /** live snapshots: reported separately, never counted as historical data */
  options_snapshot_coverage_pct?: number | null;
  options_contracts_historical_missing?: number | null;
  options_contracts_no_data?: number | null;
  pre_open_rows_info?: number;
  post_close_rows_info?: number;
  off_hours_rows_info?: number;
  missing_oi: number | null;
  stale_prices: number;
}

export interface DatasetResult {
  status: IntegrityStatus;
  reasons: string[];
  totals?: Record<string, number | null>;
  missing_pct?: number | null;
  sources?: Record<string, Record<string, unknown> & { status: IntegrityStatus; reasons?: string[]; note?: string }>;
  pairs?: Record<string, Record<string, unknown> & { status: IntegrityStatus; reasons?: string[]; primary?: boolean }>;
}

export interface AuditSummary {
  overall_status: IntegrityStatus;
  metrics: AuditMetrics;
  by_index: Record<string, { status: IntegrityStatus; datasets: Record<string, DatasetResult> }>;
}

export interface DayRow {
  date: string;
  index: string;
  dataset: string;
  source: string | null;
  trading_day: boolean;
  expected: number;
  actual: number;
  missing: number;
  coverage_pct?: number | null;
  historical_coverage_pct?: number | null;
  snapshot_coverage_pct?: number | null;
  contracts_basis?: string | null;
  expiry?: string | null;
  priced_snapshot?: number;
  actual_decisions?: DemaActual | null;
  missing_ranges?: string[];
  errors: number;
  warnings: number;
  status: IntegrityStatus;
}

export interface AuditIssue {
  dataset: string;
  code: string;
  severity: "ERROR" | "WARNING" | "INFO";
  index: string;
  date: string;
  ts: string | null;
  source: string | null;
  contract: string | null;
  count: number;
  detail: string;
  values: Record<string, unknown>;
}

export interface DemaTraceItem { item: string; status: IntegrityStatus; module: string; finding: string }
export interface DemaActual {
  ran?: boolean;
  error?: string | null;
  decisions?: number;
  trades?: number;
  rejected?: number;
  priced_historical?: number;
  priced_snapshot?: number;
  unpriced?: number;
  unpriced_reasons?: Record<string, number>;
  contract_mismatch?: number;
  price_kind_mismatch?: number;
  unpriced_mismatch?: number;
  days_not_run?: number;
  historical_pct?: number | null;
}
export interface DemaIndexResult {
  status: IntegrityStatus;
  reasons: string[];
  decisions?: number;
  priced?: number;
  unpriceable?: number;
  executable_coverage_pct?: number | null;
  priced_historical?: number;
  priced_snapshot?: number;
  historical_coverage_pct?: number | null;
  snapshot_priced_pct?: number | null;
  /** decisions whose contract / expected candle is missing from historical data (incl. snapshot-only): FAIL */
  data_gap_decisions?: number;
  data_gap_pct?: number | null;
  /** structural: the next 5-minute option price point is beyond the backtest's lag (not missing data) */
  grid_lag_decisions?: number;
  grid_lag_pct?: number | null;
  no_trade_decisions?: number;
  /** historical priceability among the decisions a complete 5-minute dataset could price */
  data_completeness_pct?: number | null;
  unpriced_reasons?: Record<string, number>;
  actual?: DemaActual;
  live_override_sessions?: number;
  live_override_minutes_differing?: number;
  entry_lag_median_s?: number | null;
  prior_price_age_median_s?: number | null;
  prior_price_older_than_60s_pct?: number | null;
  contract_not_in_data?: number;
  gap_flagged_bars?: number;
  lag_limit_s?: number;
  sessions?: number;
  sessions_by_source?: Record<string, number>;
}
export interface DemaResult {
  status: IntegrityStatus;
  config_version: number | null;
  trace: DemaTraceItem[];
  by_index: Record<string, DemaIndexResult>;
  source_proposal?: { current_behaviour: string; effect: string; proposal: string[]; status: string };
}

export interface AuditRun {
  id: string;
  status: AuditRunStatus;
  overall_status: IntegrityStatus | null;
  symbol_codes: string[];
  category: AuditCategory;
  datasets: string[];
  from_date: string;
  to_date: string;
  progress_done: number;
  progress_total: number;
  current_step: string | null;
  error: string | null;
  created_by: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  metrics: AuditMetrics | null;
  issues_stored: number;
  issues_truncated: boolean;
  summary?: AuditSummary | null;
  dema?: DemaResult | null;
  day_rows?: DayRow[];
  issue_counts?: { index: string; dataset: string; code: string; severity: string; count: number }[];
  params?: Record<string, unknown>;
}

export interface AuditRequest { underlying: AuditUnderlying; from_date: string; to_date: string; category: AuditCategory }
export interface IssueQuery { severity?: string; dataset?: string; index?: string; code?: string; day?: string; offset?: number; limit?: number }
export interface IssuePage { total: number; offset: number; limit: number; truncated: boolean; items: AuditIssue[] }

const BASE = "/oi-backfill/integrity-audit";

export const oiIntegrityAuditService = {
  async start(body: AuditRequest): Promise<AuditRun> {
    const { data } = await api.post<AuditRun>(`${BASE}/runs`, body);
    return data;
  },
  async listRuns(limit = 20): Promise<AuditRun[]> {
    const { data } = await api.get<AuditRun[]>(`${BASE}/runs`, { params: { limit } });
    return data;
  },
  async getRun(id: string): Promise<AuditRun> {
    const { data } = await api.get<AuditRun>(`${BASE}/runs/${id}`);
    return data;
  },
  async issues(id: string, q: IssueQuery = {}): Promise<IssuePage> {
    const params = Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== ""));
    const { data } = await api.get<IssuePage>(`${BASE}/runs/${id}/issues`, { params });
    return data;
  },
  async cancel(id: string): Promise<AuditRun> {
    const { data } = await api.post<AuditRun>(`${BASE}/runs/${id}/cancel`);
    return data;
  },
  /** Downloads the CSV audit report (generated server-side from the stored run). */
  async downloadReport(id: string): Promise<void> {
    const { data, headers } = await api.get<globalThis.Blob>(`${BASE}/runs/${id}/report.csv`, { responseType: "blob" });
    const disp = String((headers as Record<string, unknown>)["content-disposition"] ?? "");
    const name = /filename="?([^"]+)"?/.exec(disp)?.[1] ?? `breeze_data_audit_${id}.csv`;
    const g = globalThis;
    const blob = data instanceof g.Blob ? data : new g.Blob([String(data)], { type: "text/csv" });
    const url = g.URL.createObjectURL(blob);
    const a = g.document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    g.URL.revokeObjectURL(url);
  },
};

export default oiIntegrityAuditService;
